import $ from 'jquery';
import throttle from 'lodash/throttle';
import gsap from 'gsap';

/** Internal Dependencies */
import { NCST } from '../';
import { ControllerComponent } from './ControllerComponent';

export default class Navigation extends ControllerComponent {
	constructor() {
		super();

		this.elements = {
			nav: $( '#website-nav' ),
			navContainer: $( '.website-nav__container' ),
			navFirstChild: $(
				'.website-nav__container > .menu > li:first-child'
			),
			navLastChild: $(
				'.website-nav__container > .menu > li:last-child'
			),
			headerContentArea: $( '#site-header__content-area' ),
			headerContentAreaToggle: $( '.site-header_content-toggle' ),
			compactMasthead: $( '.compact-masthead' ),
		};

		this.state = {
			isFixed: false,
			isMobileNav: false,
			showHeaderContentArea: false,
		};

		this.prevScrollTop = null;

		const headerContentAreaAnimationSettings = {
			paused: true,
			onStart: this.beforeShowHeaderContentArea.bind(this),
			onComplete: this.afterShowHeaderContentArea.bind(this),
			onReverseComplete: this.afterHideHeaderContentArea.bind(this),
		};

		this.animations = {
			headerContentArea: gsap.timeline(
				headerContentAreaAnimationSettings
			),
		};
	}

	init() {
		if (
			this.elements.nav.length === 0 ||
			this.elements.nav.hasClass( 'is-static' )
		) {
			return;
		}

		const scrollMonitor = NCST.controllers.scrollmonitor;

		// Setup mobile/desktop nav switching
		this.setNavState();
		NCST.elements.$win.on( 'ncst_resized', this.setNavState.bind( this ) );

        // very small delay to wait for page to load before creating watcher
        // fixes a weird race condition on pageload with anchor links in the nav
        setTimeout(function() {
            // Create scroll watcher to handle fixed nav state
            const nav = scrollMonitor.createWatcher(
                'websiteNav',
                this.elements.nav
            );
            nav.lock(); // ensure that we're always watching the pre-fixed location
            nav.stateChange( this.setFixedState.bind( this ) );
            this.setFixedState();
        }.bind(this), 50);  // 50ms

		//Init mobile nav toggle
		this.elements.headerContentAreaToggle.click(
			this.headerContentAreaToggle.bind( this )
		);

		// Setup tween for mobile nav animation
		this.animations.headerContentArea.add(
			gsap.to(this.elements.headerContentArea, {duration: 0.3, scaleX: 1, scaleY: 1, opacity: 1})
		);
	}

	setFixedState() {
		// If the mobile nav is up or compact masthead exists, we shouldn't be changing anything
		if ( this.state.showHeaderContentArea || this.elements.compactMasthead ) return;

		const nav = NCST.controllers.scrollmonitor.getWatcher( 'websiteNav' );

		this.setState( {
			isFixed: nav.isAboveViewport,
		} );
	}

	setNavState() {
		this.hide( this.elements.headerContentAreaToggle );
		this.show( this.elements.navContainer );

		const isMobileNav = this.isStacked(
			this.elements.navFirstChild,
			this.elements.navLastChild
		);

		if ( isMobileNav ) {
			this.show( this.elements.headerContentAreaToggle );
			this.hide( this.elements.navContainer );
		}

		this.setState( {
			isMobileNav: isMobileNav,
			showHeaderContentArea:
				isMobileNav && this.state.showHeaderContentArea,
		} );
	}

	headerContentAreaToggle() {
		this.setState( {
			showHeaderContentArea: ! this.state.showHeaderContentArea,
		} );
	}

	render() {
		if (
			! this.state.showHeaderContentArea &&
			this.animations.headerContentArea.progress() == 1
		) {
			// GSAP doesn't provide a reverse start handler, so do some setup here.
			NCST.elements.$htmlBody.css( {
				height: '',
				overflow: '',
			} );
			//resets the page position to where the user was
			NCST.elements.$win.scrollTop( this.prevScrollTop );
			this.prevScrollTop = null;

			// Now close the content area
			this.animations.headerContentArea.reverse();
			NCST.controllers.mobileMenu.reset();
		} else if (
			this.state.showHeaderContentArea &&
			this.animations.headerContentArea.progress() == 0
		) {
			this.animations.headerContentArea.play();
		}

		this.elements.headerContentAreaToggle
			.get( 0 )
			.setAttribute( 'aria-expanded', this.state.showHeaderContentArea );
		NCST.elements.$htmlBody.toggleClass(
			'header-content-active',
			this.state.showHeaderContentArea
		);
		NCST.elements.$body.toggleClass( 'has-fixed-nav', this.state.isFixed );
		NCST.elements.$body.toggleClass(
			'is-mobile-nav',
			this.state.isMobileNav
		);
		this.elements.headerContentAreaToggle.toggle( this.state.isMobileNav );
		this.elements.navContainer.toggle( ! this.state.isMobileNav );
	}

	beforeShowHeaderContentArea() {
		this.prevScrollTop = NCST.elements.$win.scrollTop();
		this.show( this.elements.headerContentArea );
	}

	afterShowHeaderContentArea() {
		NCST.elements.$htmlBody.css( {
			height: '100%',
			overflow: 'hidden',
		} ); //prevents scrolling behind menu

		NCST.elements.$win.trigger( 'ncst_header_content_shown' );
	}

	afterHideHeaderContentArea() {
		this.hide( this.elements.headerContentArea );
	}

	isStacked( el1, el2 ) {
		var el1Bottom = el1.offset().top + el1.height();
		var el2Top = el2.offset().top;

		// Elements are stacked if the bottom of el1 is above the top of el2.
		return Math.ceil( el1Bottom ) <= Math.ceil( el2Top );
	}

	hide( $el ) {
		$el.css( { visibility: 'hidden' } );
		$el.hide();
	}

	show( $el ) {
		$el.css( { visibility: 'visible' } );
		$el.show();
	}
}
